'use client';

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useBlockPart,
  useLearningSource,
  type LearningItem,
  type LearningRow,
} from '@akademi/site-builder-runtime';
import {
  resolveCourseCard,
  type CourseCardVariant,
  type ButtonRadius,
  type CertificateDisplay,
  type PopoverPosition,
  type ProgressTextPlace,
} from '../cards/registry';
import { getGridSpec, type CardGridSpec } from '../cards/gridSpecs';
import { learningItemToCardProps } from '../cards/learningItemToCardProps';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { StaleIndicator } from '../_shared/StaleIndicator';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { prettify } from '../_shared/prettify';
import { ContentRail, type ContentRailScrollMode } from '../ui/ContentRail';

/**
 * The workhorse rail for the LMS catalog. Renders N course cards
 * fetched from {@code LearningSource.fetchRow(source)}, dispatched
 * through {@link resolveCourseCard} so themes can swap the card.
 */
export type CourseCardStyle =
  | 'default'
  | 'compact'
  | 'wide'
  | 'minimal'
  | 'cinematic'
  | 'marketplace'
  | 'academic'
  | 'udemy'
  | 'classicHigh'
  | 'classicNetflixZoom'
  | 'borderless'
  | 'custom';

export interface CourseRowProps {
  title?: string;
  /** When set, the row title links to this URL. Also becomes the
   *  'See all' link's target when {@link seeAllHref} is left empty. */
  titleHref?: string;
  source?: string;
  cardStyle?: CourseCardStyle;
  /** Show the course-card star rating. Defaults to shown. */
  showRating?: boolean;
  /** CTA button corner radius. Empty/default inherits the theme radius. */
  buttonRadius?: ButtonRadius;
  /** How the certificate marker renders on the udemy card's hover popover:
   *  badge image, text pill, or both. Defaults to both. */
  certificateDisplay?: CertificateDisplay;
  /** Autoplay the preview clip inside the udemy card's hover popover.
   *  Defaults to false (static poster + play badge). */
  autoPlayPreview?: boolean;
  /** Where the udemy card's hover popover opens: left, center (overlays the
   *  card), or right (default, falling back to left when there's no room). */
  popoverPosition?: PopoverPosition;
  /** When true (default), the udemy card's hover-popover title links to
   *  the course, same as the card face. */
  popoverTitleLink?: boolean;
  /** Progress bar shown above the udemy card's footer for courses carrying
   *  user progress. {@code show} (default true) toggles the bar;
   *  {@code showText} (default false) adds the percentage label;
   *  {@code textPlace} positions that label beside or on top of the track. */
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
  /** How many cards are visible at once in a wide (desktop) row, 5–10.
   *  Higher values pack more, narrower cards into the same width; narrower
   *  screens still fit fewer automatically. Only affects grid-based card
   *  styles — 'minimal' (list) and 'cinematic' (viewport-relative poster
   *  rail) size themselves independently and ignore this. Leave unset to
   *  use the card style's own default width. */
  cardsPerRow?: number;
  limit?: number;
  seeAllLabel?: string;
  seeAllHref?: string;
  /** href template for individual course cards. When unset, inherits the
   *  card's own default ({@code /lxp/course/{id}}). */
  courseHrefTemplate?: string;
  /** Badge image URL for the certificate overlay on card thumbnails.
   *  Sourced from {@code accountConfig.ui.course_card_badge}. */
  certificateBadgeUrl?: string;
  style?: CSSProperties;
  /** When true, renders the section even if the source returns no items. */
  showWhenDataIsNull?: boolean;
  /** Layout / scroll mode. {@code 'grid'} (default) keeps the selected
   *  card style's own native layout (grid, list, or rail). Any other
   *  value forces the row into a horizontally-scrolling rail regardless
   *  of card style: {@code 'drag'} = free mouse grab-to-scroll,
   *  {@code 'buttons'} = arrows-only stepper, {@code 'scrollbar'} /
   *  {@code 'wheel'} = wheel-driven with/without a visible scrollbar. */
  scrollMode?: 'grid' | 'drag' | 'buttons' | 'scrollbar' | 'wheel';
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

const DEFAULT_SOURCE = 'popular';
const DEFAULT_LIMIT = 12;
const MIN_LIMIT = 1;
const MAX_LIMIT = 12;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function resolveLimit(raw: number | undefined): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : DEFAULT_LIMIT;
  return clamp(n, MIN_LIMIT, MAX_LIMIT);
}

function resolveSource(raw: string | undefined): string {
  return (raw ?? '').trim() || DEFAULT_SOURCE;
}

/**
 * Turn a grid spec's {@code minCardWidth} into a sensible fixed rail-card
 * width. {@code minCardWidth} is only a CSS Grid lower bound — grid columns
 * (`minmax(minCardWidth, 1fr)`) actually stretch far wider to fill the row.
 * A rail card has a genuinely fixed width, so reusing that raw minimum
 * verbatim renders every card much narrower than the same card style looks
 * in its native grid layout. Clamp into a responsive range instead, the
 * same way the 'cinematic' style sizes its own native rail cards
 * ({@code clamp(220px, 35vw, 320px)}).
 */
function railWidthFromMinCardWidth(minCardWidth: number): string {
  const min = Math.max(minCardWidth, 220);
  const max = Math.max(minCardWidth * 1.8, 320);
  return `clamp(${min}px, 26vw, ${max}px)`;
}

const MIN_CARDS_PER_ROW = 5;
const MAX_CARDS_PER_ROW = 10;
/** Reference row width used to translate an admin-chosen "cards per row"
 *  count into a concrete card width — matches the theme's default section
 *  max-width token (see {@code _shared/styles.ts}'s {@code sectionInner}). */
const REFERENCE_ROW_WIDTH = 1200;
const REFERENCE_GAP = 16;

function resolveCardsPerRow(raw: number | undefined): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  return clamp(Math.round(raw), MIN_CARDS_PER_ROW, MAX_CARDS_PER_ROW);
}

/** Per-card width that fits {@code count} cards (plus gaps) across one
 *  {@link REFERENCE_ROW_WIDTH}-wide row. Floored so even the densest
 *  (10-per-row) setting stays legible. */
function minCardWidthForVisibleCount(count: number): number {
  const usable = REFERENCE_ROW_WIDTH - REFERENCE_GAP * (count - 1);
  return Math.max(120, Math.floor(usable / count));
}

/** Rail-card width for an explicit "cards per row" choice — unlike
 *  {@link railWidthFromMinCardWidth}, this trusts the computed width
 *  directly (no 220px safety floor) since it reflects a deliberate admin
 *  choice rather than an automatic conversion of a card style's own grid
 *  lower-bound default. Still {@code vw}-relative so it narrows further on
 *  small screens instead of clipping. */
function railWidthForVisibleCount(count: number): string {
  const w = minCardWidthForVisibleCount(count);
  return `clamp(${w}px, ${(100 / count).toFixed(2)}vw, ${Math.round(w * 1.4)}px)`;
}

export function CourseRow({
  title,
  titleHref,
  cardStyle = 'default',
  showRating = true,
  buttonRadius,
  certificateDisplay,
  autoPlayPreview,
  popoverPosition,
  popoverTitleLink,
  progressBar,
  seeAllLabel,
  seeAllHref,
  source: rawSource,
  limit: rawLimit,
  cardsPerRow: rawCardsPerRow,
  courseHrefTemplate,
  certificateBadgeUrl,
  style,
  paddingOuter,
  headingLine,
  paddingInner,
  showWhenDataIsNull = false,
  scrollMode = 'grid',
}: CourseRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const learning = useLearningSource();
  const source = resolveSource(rawSource);
  const limit = resolveLimit(rawLimit);
  const titleLinkScope = `lms-courserow-title-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const state = useAsyncResource<LearningRow>(
    async (signal) => {
      if (!learning) return null;
      return await learning.fetchRow({ source, limit, signal });
    },
    [learning, source, limit],
  );

  // Infinite loading: pages appended beyond the first (useAsyncResource-
  // managed) fetch, driven by the rail scrolling near its end (see
  // ContentRail's onReachEnd) or the grid/list scrolling into view (see
  // the IntersectionObserver sentinel in CardContainer) — mirrors the
  // legacy LMS's PageSliderSection/CourseSliderBox pattern of fetching
  // the next `offset` page once the visitor nears the end of what's
  // already loaded, instead of a single fixed-size fetch.
  const [morePages, setMorePages] = useState<LearningItem[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  useEffect(() => {
    setMorePages([]);
    setLoadingMore(false);
    setExhausted(false);
  }, [learning, source, limit]);

  const baseItems: LearningItem[] = state.kind === 'loaded' ? state.data.items : [];
  const total = state.kind === 'loaded' ? state.data.total : undefined;
  const courses: LearningItem[] = baseItems.length > 0 ? [...baseItems, ...morePages] : baseItems;

  // Latest-value refs so `loadMore` stays referentially stable per feed
  // (learning/source/limit) instead of churning on every appended page —
  // ContentRail relies on that stability to avoid re-subscribing listeners.
  const loadingMoreRef = useRef(loadingMore);
  const exhaustedRef = useRef(exhausted);
  const itemsRef = useRef(courses);
  const totalRef = useRef(total);
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
    exhaustedRef.current = exhausted;
    itemsRef.current = courses;
    totalRef.current = total;
  });

  const loadMore = useCallback(() => {
    if (!learning || loadingMoreRef.current || exhaustedRef.current) return;
    const loadedSoFar = itemsRef.current;
    // Nothing loaded yet (first page still in flight, or empty) — nothing
    // to page past.
    if (loadedSoFar.length === 0) return;
    const knownTotal = totalRef.current;
    if (typeof knownTotal === 'number' && loadedSoFar.length >= knownTotal) {
      setExhausted(true);
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMore(true);
    learning
      .fetchRow({ source, limit, offset: loadedSoFar.length })
      .then((next) => {
        if (!next || next.items.length === 0) {
          setExhausted(true);
        } else {
          setMorePages((prev) => [...prev, ...next.items]);
        }
      })
      .catch(() => setExhausted(true))
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [learning, source, limit]);

  const authorTitle = typeof title === 'string' && title.trim() ? title.trim() : undefined;
  const serverLabel = state.kind === 'loaded' ? state.data.label : undefined;
  // Plain-text title — reused for the visual heading AND the grid's aria-label
  // (which must be a string, not the JSX node).
  const labelText = authorTitle || serverLabel || prettify(source);
  // Pass the plain string so SectionShell renders it directly inside its
  // <h2> (matching the other blocks) instead of nesting a styled <span>.
  const label = labelText;

  const resolvedTitleHref =
    typeof titleHref === 'string' && titleHref.trim() ? titleHref.trim() : undefined;
  // 'See all' only renders when its own href is explicitly set — no
  // fallback to the title link, so leaving it empty always hides it.
  const resolvedSeeAllHref =
    typeof seeAllHref === 'string' && seeAllHref.trim() ? seeAllHref.trim() : undefined;
  const resolvedSeeAllLabel =
    (typeof seeAllLabel === 'string' && seeAllLabel.trim()) || t('shared.seeAll');
  const action: ReactNode = resolvedSeeAllHref ? (
    <PortalLink
      href={resolvedSeeAllHref}
      style={{
        color: 'var(--color-brand-600, #d6007f)',
        fontWeight: 'var(--font-weight-medium, 500)',
        textDecoration: 'none',
      }}
    >
      {resolvedSeeAllLabel} →
    </PortalLink>
  ) : undefined;

  // Wraps the heading in a title-scoped link when a title href is set —
  // inherits the heading's own color at rest and switches to brand on
  // hover, same as the udemy card's popover title link
  // ('.lms-udemy-popover-title-link').
  const linkTitle = (node: ReactNode): ReactNode =>
    resolvedTitleHref ? (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: `.${titleLinkScope} { color: inherit; text-decoration: none; }
.${titleLinkScope}:hover { color: var(--color-brand-600, #d6007f); }`,
          }}
        />
        <PortalLink href={resolvedTitleHref} className={titleLinkScope}>
          {node}
        </PortalLink>
      </>
    ) : (
      node
    );

  const titleNode: ReactNode = linkTitle(
    state.kind === 'loaded' && state.data.stale ? (
      <>
        {label}
        <span style={{ marginLeft: 12, display: 'inline-block', verticalAlign: 'middle' }}>
          <StaleIndicator />
        </span>
      </>
    ) : (
      label
    ),
  );

  if (!learning) {
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={linkTitle(label)}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <AdapterPlaceholder blockType="CourseRow" adapterName="LearningSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={titleNode}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <SkeletonRow count={limit} layout="rail" />
      </SectionShell>
    );
  }

  if (courses.length === 0) {
    if (!showWhenDataIsNull) return null;
    return (
      <SectionShell
        headingLine={headingLine}
        style={style}
        title={titleNode}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <EmptyState
          title={t('courseRow.empty.title')}
          hint={t('courseRow.empty.hintSource')}
          diagnostic={source}
        />
      </SectionShell>
    );
  }

  const Card = resolveCourseCard(cardStyle as CourseCardVariant);
  const nativeSpec = getGridSpec(cardStyle);
  const resolvedCardsPerRow = resolveCardsPerRow(rawCardsPerRow);

  // 'grid' keeps the card style's native layout (grid/list/rail). Any
  // other scrollMode value forces a horizontally-scrolling rail
  // regardless of card style, per the block schema's documented
  // behavior — e.g. picking 'buttons' on the (grid-kind) 'udemy' style
  // turns it into a paginated rail instead of a static grid.
  //
  // An explicit "cards per row" choice overrides the card style's own
  // default column width — only meaningful for grid-kind styles ('minimal'
  // is a vertical list, 'cinematic' sizes its own viewport-relative rail).
  const gridSpec: CardGridSpec =
    nativeSpec.kind === 'grid'
      ? scrollMode !== 'grid'
        ? {
            kind: 'rail',
            cardWidth: resolvedCardsPerRow
              ? railWidthForVisibleCount(resolvedCardsPerRow)
              : railWidthFromMinCardWidth(nativeSpec.minCardWidth),
            gap: nativeSpec.gap,
          }
        : resolvedCardsPerRow
          ? { ...nativeSpec, minCardWidth: minCardWidthForVisibleCount(resolvedCardsPerRow) }
          : nativeSpec
      : nativeSpec;
  // Native rail card styles (e.g. cinematic) fall back to free-drag
  // scrolling when the row is left on its 'grid' default (no explicit
  // override requested) — ContentRail has no 'grid' mode of its own.
  const railScrollMode: ContentRailScrollMode = scrollMode === 'grid' ? 'drag' : scrollMode;

  // In paginated ('buttons') mode on a rail, the prev/next arrows live in
  // the rail's own header row (title inline-start, arrows inline-end) —
  // matching LearningJourneys / TestimonialsBento. Suppress SectionShell's
  // header there so the title isn't rendered twice.
  const headerArrows = gridSpec.kind === 'rail' && scrollMode === 'buttons';

  return (
    <SectionShell
      headingLine={headingLine}
      style={style}
      title={headerArrows ? undefined : titleNode}
      action={headerArrows ? undefined : action}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <CardContainer
        spec={gridSpec}
        label={labelText}
        scrollMode={railScrollMode}
        heading={headerArrows ? titleNode : undefined}
        headerAction={headerArrows ? action : undefined}
        onReachEnd={loadMore}
        loadingMore={loadingMore}
      >
        {courses.map((c) => (
          <Card
            key={c.id}
            showRating={showRating}
            buttonRadius={buttonRadius}
            certificateDisplay={certificateDisplay}
            autoPlayPreview={autoPlayPreview}
            popoverPosition={popoverPosition}
            popoverTitleLink={popoverTitleLink}
            progressBar={progressBar}
            course={learningItemToCardProps(c, {
              ...(courseHrefTemplate ? { hrefTemplate: courseHrefTemplate } : {}),
              certificateBadgeUrl,
            })}
          />
        ))}
      </CardContainer>
    </SectionShell>
  );
}

function CardContainer({
  spec,
  children,
  label,
  scrollMode,
  heading,
  headerAction,
  onReachEnd,
  loadingMore,
}: {
  spec: CardGridSpec;
  children: ReactNode;
  label: string;
  scrollMode?: ContentRailScrollMode;
  heading?: ReactNode;
  headerAction?: ReactNode;
  /** Pagination hook — see {@link CourseRow}'s `loadMore`. */
  onReachEnd?: () => void;
  loadingMore?: boolean;
}) {
  // Grid/list layouts have no native "scrolled near the end" signal (unlike
  // the rail, which already tracks its own horizontal scroll position) —
  // an IntersectionObserver sentinel placed after the content triggers the
  // same pagination once the visitor scrolls the *page* down to it. It's a
  // sibling of the grid/list, never a grid column or list item, so it
  // can't affect card sizing.
  const sentinelRef = useEndSentinel(onReachEnd);
  const part = useBlockPart();

  if (spec.kind === 'list') {
    const arr = Array.isArray(children) ? children : [children];
    return (
      <>
        <ul
          {...part('list')}
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          }}
        >
          {arr.map((child, i) => (
            <li key={i} {...part('item', i)}>
              {child}
            </li>
          ))}
        </ul>
        <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
        {loadingMore && <LoadMoreIndicator />}
      </>
    );
  }

  if (spec.kind === 'rail') {
    return (
      <ContentRail
        ariaLabel={label || 'Courses'}
        itemWidth={spec.cardWidth ?? '320px'}
        gap={spec.gap ?? 'clamp(10px, 1.5vw, 16px)'}
        scrollMode={scrollMode}
        heading={heading}
        headerAction={headerAction}
        // No partially-visible next card at the trailing edge — every card
        // shows in full; the prev/next arrows (or drag) are the only hint
        // that more content follows.
        peek={false}
        onReachEnd={onReachEnd}
        loadingMore={loadingMore}
      >
        {children}
      </ContentRail>
    );
  }

  const gap = spec.gap ?? 'clamp(12px, 2vw, 16px)';
  const minW = spec.minCardWidth;

  // Plain wrapping grid: every row gets its natural (auto) height, so a
  // row that doesn't fill completely wraps in full rather than being
  // clipped mid-card. No overflow property here — this div never clips
  // its own content — but the horizontal padding still matters: a card
  // whose hover effect grows past its own grid cell (lift/shadow, or a
  // Netflix-style zoom scale) has nowhere to bleed into at the very
  // first/last column if the block's own paddingOuter is 0, since
  // *something* upstream (the page shell) generally clips horizontal
  // overflow to avoid a stray scrollbar. The negative margin cancels the
  // padding back out of the surrounding layout — same trick ContentRail
  // uses — so this still lines up with paddingOuter exactly at rest.
  return (
    <>
      <div
        {...part('list')}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(min(${minW}px, 100%), 1fr))`,
          gap,
          paddingInline: 'clamp(1.5rem, 4vw, 3rem)',
          marginInline: 'calc(-1 * clamp(1.5rem, 4vw, 3rem))',
        }}
      >
        {children}
      </div>
      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
      {loadingMore && <LoadMoreIndicator />}
    </>
  );
}

/** Ref-callback hook: attaches an IntersectionObserver to whatever DOM node
 *  it's given and calls `onReachEnd` once that node scrolls within 600px of
 *  the viewport — the grid/list equivalent of the rail's scroll-proximity
 *  check. Kept as a ref (not a dependency) so passing a new `onReachEnd`
 *  closure each render doesn't tear down and recreate the observer. */
function useEndSentinel(onReachEnd?: () => void): (node: HTMLDivElement | null) => void {
  const onReachEndRef = useRef(onReachEnd);
  useEffect(() => {
    onReachEndRef.current = onReachEnd;
  });
  const observerRef = useRef<IntersectionObserver | null>(null);

  return useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onReachEndRef.current?.();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);
}

function LoadMoreIndicator() {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div
      role="status"
      aria-label={t('courseRow.loadingMore')}
      style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `@keyframes lms-courserow-spin { to { transform: rotate(360deg); } }`,
        }}
      />
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: '2px solid var(--color-border-subtle, rgba(0,0,0,0.12))',
          borderTopColor: 'var(--color-brand-500, #6366f1)',
          animation: 'lms-courserow-spin 700ms linear infinite',
        }}
      />
    </div>
  );
}
